(() => {
  'use strict';
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  class Rotator extends HTMLElement {
    setup() {
      this.controller = new AbortController();
      this.paused = motion.matches;
      this.hovered = false; this.focused = false; this.visible = true;
      this.listen = (node, type, fn, options = {}) => node.addEventListener(type, fn, { ...options, signal: this.controller.signal });
      this.listen(this, 'mouseenter', () => { this.hovered = true; this.restart(); });
      this.listen(this, 'mouseleave', () => { this.hovered = false; this.restart(); });
      this.listen(this, 'focusin', () => { this.focused = true; this.restart(); });
      this.listen(this, 'focusout', (event) => { this.focused = this.contains(event.relatedTarget); this.restart(); });
      this.listen(document, 'visibilitychange', () => this.restart());
      this.listen(motion, 'change', () => { this.paused = motion.matches; this.updatePause(); this.restart(); });
      this.intersection = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.restart(); });
      this.intersection.observe(this);
      this.updatePause();
    }
    updatePause() {
      const button = this.querySelector('[data-carousel-pause], [data-hero-pause]');
      if (button) { button.textContent = this.paused ? 'Play' : 'Pause'; button.setAttribute('aria-label', this.paused ? 'Play automatic slides' : 'Pause automatic slides'); button.setAttribute('aria-pressed', String(this.paused)); }
    }
    togglePause() { this.paused = !this.paused; this.updatePause(); this.restart(); }
    restart() {
      clearInterval(this.timer);
      if (this.dataset.autoplay !== 'true' || this.paused || this.hovered || this.focused || !this.visible || document.hidden || this.count < 2 || window.Shopify?.designMode) return;
      this.timer = setInterval(() => this.next(true), Math.max(3, Number(this.dataset.speed) || 5) * 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); this.controller?.abort(); this.intersection?.disconnect(); this.resize?.disconnect(); }
  }
  class HamdooHero extends Rotator {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.count = this.slides.length; this.index = 0;
      this.setup();
      this.tabIndex = 0;
      const dots = this.querySelector('[data-hero-dots]');
      dots?.replaceChildren();
      this.slides.forEach((slide, i) => {
        slide.setAttribute('aria-label', `${i + 1} of ${this.count}`);
        if (dots) { const button = document.createElement('button'); button.type = 'button'; button.className = 'h-dot'; button.setAttribute('aria-label', `Show slide ${i + 1}`); this.listen(button, 'click', () => this.show(i, true)); dots.append(button); }
      });
      this.listen(this, 'click', event => {
        if (event.target.closest('[data-hero-prev]')) this.show(this.index - 1, true);
        if (event.target.closest('[data-hero-next]')) this.show(this.index + 1, true);
        if (event.target.closest('[data-hero-pause]')) this.togglePause();
      });
      this.listen(this, 'keydown', event => {
        if (event.target !== this && !event.target.matches('.h-dot, [data-hero-prev], [data-hero-next]')) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1), true); }
      });
      this.listen(this, 'touchstart', event => { const t = event.touches[0]; this.touchStart = { x: t.clientX, y: t.clientY }; }, { passive: true });
      this.listen(this, 'touchend', event => {
        if (!this.touchStart) return;
        const t = event.changedTouches[0], dx = t.clientX - this.touchStart.x, dy = t.clientY - this.touchStart.y;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.4) { if (event.cancelable) event.preventDefault(); this.show(this.index + (dx < 0 ? 1 : -1), true); }
        this.touchStart = null;
      });
      this.listen(document, 'shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === String(event.detail?.blockId));
        if (index >= 0) { this.paused = true; this.updatePause(); this.show(index, false); }
      });
      this.resize = new ResizeObserver(() => {
        // Reserve the tallest enabled slide so changing slides never moves content below it.
        if (this.slides.length) this.style.minHeight = Math.max(...this.slides.map(slide => parseFloat(getComputedStyle(slide).height) || 0)) + 'px';
      });
      this.resize.observe(this);
      this.show(0, false);
    }
    next() { this.show(this.index + 1, false); }
    show(index, manual) {
      if (!this.count) return;
      const previous = this.index; this.index = (index + this.count) % this.count;
      this.slides.forEach((slide, i) => { slide.hidden = i !== this.index; slide.setAttribute('aria-hidden', String(i !== this.index)); });
      this.querySelectorAll('[data-hero-dots] button').forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
      const status = this.querySelector('[data-hero-status]');
      if (status) { status.setAttribute('aria-live', manual ? 'polite' : 'off'); status.textContent = `Slide ${this.index + 1} of ${this.count}`; }
      if (previous !== this.index && !motion.matches) {
        const frames = this.dataset.transition === 'fade' ? [{ opacity: 0 }, { opacity: 1 }] : [{ transform: `translateX(${index > previous ? 3 : -3}%)`, opacity: .6 }, { transform: 'translateX(0)', opacity: 1 }];
        this.slides[this.index].animate(frames, { duration: 350, easing: 'ease-out' });
      }
      this.restart();
    }
  }
  class HamdooCarousel extends Rotator {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;
      this.continuous = this.dataset.continuous === 'true' && this.dataset.layout === 'carousel';
      this.index = 0; this.count = 0; this.setup();
      const imageFallback = img => { const src=img.closest('[data-fallback-src]')?.dataset.fallbackSrc;if(src && !img.dataset.fallbackApplied){img.dataset.fallbackApplied='true';img.removeAttribute('srcset');img.src=src;} };
      this.listen(this.track,'error',event=>{if(event.target.matches('.h-category-image'))imageFallback(event.target);},{capture:true});
      this.track.querySelectorAll('.h-category-image').forEach(img=>{if(img.complete&&!img.naturalWidth)imageFallback(img);});
      if (this.continuous) { this.connectContinuous(); return; }
      this.querySelector('[data-carousel-dots]')?.replaceChildren();
      this.listen(this, 'click', event => {
        if (event.target.closest('[data-carousel-next]')) this.next(false);
        if (event.target.closest('[data-carousel-prev]')) this.go(this.index - 1, true);
        if (event.target.closest('[data-carousel-pause]')) this.togglePause();
      });
      this.listen(this.track, 'keydown', event => {
        if (event.target !== this.track || this.dataset.layout !== 'carousel') return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1), true); }
        if (event.key === 'Home' || event.key === 'End') { event.preventDefault(); this.go(event.key === 'Home' ? 0 : this.count - 1, true); }
      });
      this.listen(this.track, 'pointerdown', () => { if(this.track.classList.contains('h-category-track')){this.paused=true;this.updatePause();this.restart();} }, { passive:true });
      this.listen(this.track, 'wheel', () => { if(this.track.classList.contains('h-category-track')){this.paused=true;this.updatePause();this.restart();} }, { passive:true });
      this.listen(this.track, 'scroll', () => { cancelAnimationFrame(this.frame); this.frame = requestAnimationFrame(() => this.update()); }, { passive: true });
      this.listen(document, 'shopify:block:select', event => {
        if (!this.contains(event.target) || this.dataset.layout !== 'carousel') return;
        const item = event.target.closest('.h-track > *');
        if (item) { this.paused = true; this.updatePause(); this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: 'instant' }); }
      });
      this.resize = new ResizeObserver(() => this.measure()); this.resize.observe(this.track); this.measure();
    }
    connectContinuous() {
      this.originals = Array.from(this.track.querySelectorAll(':scope > .h-category:not([data-loop-clone])'));
      this.interactionUntil = 0; this.position = 0; this.lastWritten = 0; this.pointerHeld = false; this.touchHeld = false; this.drag = null; this.suppressClick = false; this.track.classList.remove('h-dragging');
      this.listen(this.track, 'scroll', () => {
        if (!this.period) return;
        const current = this.track.scrollLeft;
        if (Math.abs(current - this.lastWritten) > 1) this.position = current;
        this.normalize(); this.update();
      }, { passive: true });
      this.listen(this.track, 'wheel', () => this.interact(1400), { passive: true });
      this.listen(this.track,'touchstart',()=>{this.touchHeld=true;this.interact(1400);},{passive:true});
      const touchEnd=()=>{if(this.touchHeld){this.touchHeld=false;this.interact(1400);}};
      this.listen(window,'touchend',touchEnd,{passive:true});this.listen(window,'touchcancel',touchEnd,{passive:true});
      this.listen(this.track, 'pointerdown', event => {
        if (event.button !== 0) return;
        this.pointerHeld = true; this.interact(1400);
        this.drag = event.pointerType === 'mouse' ? { id: event.pointerId, x: event.clientX, y: event.clientY, left: this.track.scrollLeft, moved: false } : null;
      }, { passive: true });
      this.listen(this.track, 'pointermove', event => {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        const dx = event.clientX - this.drag.x, dy = event.clientY - this.drag.y;
        if (!this.drag.moved && Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy)) {
          this.drag.moved = true; this.track.setPointerCapture(event.pointerId); this.track.classList.add('h-dragging');
        }
        if (!this.drag.moved) return;
        event.preventDefault(); this.position = this.drag.left - dx; this.normalize(true);
      });
      const finish = () => {
        if (!this.pointerHeld && !this.drag) return;
        if (this.drag?.moved) { this.suppressClick = true; clearTimeout(this.clickTimer); this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 0); }
        if (this.drag && this.track.hasPointerCapture(this.drag.id)) this.track.releasePointerCapture(this.drag.id);
        this.drag = null; this.pointerHeld = false; this.track.classList.remove('h-dragging'); this.interact(1400);
      };
      this.listen(window, 'pointerup', finish);
      this.listen(window, 'pointercancel', finish);
      this.listen(this.track, 'dragstart', event => event.preventDefault());
      this.listen(this.track, 'click', event => {
        if (this.suppressClick) { event.preventDefault(); event.stopImmediatePropagation(); this.suppressClick = false; return; }
        const clone = event.target.closest('[data-loop-clone]');
        if (clone) { event.preventDefault(); this.originals[Number(clone.dataset.loopClone)]?.click(); }
      }, { capture: true });
      this.listen(this.track, 'keydown', event => {
        if (event.target !== this.track) return;
        if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        event.preventDefault(); this.interact(1400);
        const base = this.period || 0, last = Math.max(0, (this.period || this.track.scrollWidth) - this.gap - this.track.clientWidth);
        if (event.key === 'Home') this.position = base;
        else if (event.key === 'End') this.position = base + last;
        else this.position = this.track.scrollLeft + (event.key === 'ArrowRight' ? 1 : -1) * this.stride;
        this.normalize(true); this.update();
      });
      this.listen(this, 'focusin', event => {
        // Keyboard focus visits only the original cards. Keep the focused card in view.
        const card = event.target.closest('.h-category:not([data-loop-clone])');
        if (!card) return;
        const a = card.getBoundingClientRect(), t = this.track.getBoundingClientRect();
        if (a.left < t.left || a.right > t.right) { this.position = card.offsetLeft - this.track.offsetLeft; this.normalize(true); }
      });
      this.listen(document, 'shopify:block:select', event => {
        const card = event.target.closest('.h-category:not([data-loop-clone])');
        if (card && this.contains(card)) { this.interact(2000); this.position = card.offsetLeft - this.track.offsetLeft; this.normalize(true); }
      });
      this.resize = new ResizeObserver(() => this.measureContinuous()); this.resize.observe(this.track); this.measureContinuous();
    }
    interact(delay) { this.interactionUntil = performance.now() + delay; this.restart(); }
    cloneCard(card, index) {
      const clone = document.createElement('div'); clone.className = card.className; clone.style.cssText = card.style.cssText;
      clone.dataset.loopClone = String(index); clone.setAttribute('aria-hidden', 'true');
      for (const child of card.childNodes) clone.append(child.cloneNode(true));
      clone.querySelectorAll('[id], [data-shopify-editor-block]').forEach(node => { node.removeAttribute('id'); node.removeAttribute('data-shopify-editor-block'); });
      clone.querySelectorAll('a,button,input,select,textarea,[tabindex]').forEach(node => { node.setAttribute('tabindex', '-1'); });
      clone.querySelectorAll('img').forEach(img => { img.draggable = false; });
      return clone;
    }
    measureContinuous() {
      cancelAnimationFrame(this.loopFrame);
      const logical = this.period ? ((this.track.scrollLeft - this.period) % this.period + this.period) % this.period : 0;
      this.track.querySelectorAll('[data-loop-clone]').forEach(node => node.remove());
      this.gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      this.stride = (this.originals[0]?.getBoundingClientRect().width || this.track.clientWidth) + this.gap;
      const full = this.stride * this.originals.length, max = Math.max(0, full - this.gap - this.track.clientWidth);
      this.period = max > 1 && !window.Shopify?.designMode ? full : 0;
      const cols = Math.max(1, Number(getComputedStyle(this).getPropertyValue('--active-cols')) || 1);
      this.positions = Array.from(new Set(Array.from({length:Math.max(1,Math.ceil(this.originals.length / cols))},(_,i)=>Math.min(max,i*cols*this.stride))));
      this.count = this.positions.length; this.dataset.singlePage = String(this.count <= 1);
      if (this.period) {
        const before = document.createDocumentFragment(), after = document.createDocumentFragment();
        this.originals.forEach((card,i) => {before.append(this.cloneCard(card,i));after.append(this.cloneCard(card,i));});
        this.track.prepend(before); this.track.append(after);
      }
      this.position = (this.period || 0) + Math.min(logical, max); this.normalize(true);
      const dots = this.querySelector('[data-carousel-dots]');
      if (dots && dots.children.length !== this.count) { dots.replaceChildren();this.positions.forEach((_,i)=>{const button=document.createElement('button');button.type='button';button.className='h-dot';button.setAttribute('aria-label',`Show category page ${i+1}`);this.listen(button,'click',()=>this.go(i,true));dots.append(button);}); }
      this.update(); this.restart();
    }
    normalize(write = false) {
      if (this.period) {
        const previous = this.position;
        while (this.position >= 2 * this.period) this.position -= this.period;
        while (this.position < this.period) this.position += this.period;
        if (this.drag) this.drag.left += this.position - previous;
        write ||= Math.abs(this.position - this.track.scrollLeft) > 1;
      }
      if (write) { this.track.scrollLeft = this.position; this.lastWritten = this.track.scrollLeft; }
    }
    restart() {
      if (!this.continuous) { super.restart(); return; }
      cancelAnimationFrame(this.loopFrame); clearTimeout(this.resumeTimer);
      if (!this.period || this.dataset.autoplay !== 'true' || motion.matches || this.hovered || this.focused || this.pointerHeld || this.touchHeld || !this.visible || document.hidden || window.Shopify?.designMode) return;
      const remaining = (this.interactionUntil || 0) - performance.now();
      if (remaining > 0) { this.resumeTimer = setTimeout(() => this.restart(), remaining + 20); return; }
      let previous;
      const step = now => {
        if (previous !== undefined) this.position += this.stride / Math.max(3,Number(this.dataset.speed)||5) * Math.min(50,now-previous) / 1000;
        previous = now; this.normalize(true); this.loopFrame = requestAnimationFrame(step);
      };
      this.loopFrame = requestAnimationFrame(step);
    }

    measure() {
      if (this.dataset.layout !== 'carousel') { this.count = 0; this.restart(); return; }
      const items = Array.from(this.track.children).filter(item => item.tagName !== 'P');
      const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      const cols = Math.max(1, Number(getComputedStyle(this).getPropertyValue('--active-cols')) || 1);
      const itemWidth = items[0]?.getBoundingClientRect().width || this.track.clientWidth;
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.positions = Array.from({ length: Math.max(1, Math.ceil(items.length / cols)) }, (_, i) => Math.min(max, i * cols * (itemWidth + gap)));
      this.positions = Array.from(new Set(this.positions)); this.count = this.positions.length;
      this.dataset.singlePage = String(this.count <= 1);
      const dots = this.querySelector('[data-carousel-dots]');
      if (dots) {
        // Rebuild only when page count changes, preserving keyboard focus on resize/scroll.
        if (dots.children.length !== this.count) {
          dots.replaceChildren();
          this.positions.forEach((_, i) => { const button = document.createElement('button'); button.className = 'h-dot'; button.type = 'button'; button.setAttribute('aria-label', `Show page ${i + 1}`); this.listen(button, 'click', () => this.go(i, true)); dots.append(button); });
        }
      }
      this.update(); this.restart();
    }
    update() {
      if (!this.positions?.length) return;
      this.index = this.positions.reduce((best, x, i) => Math.abs(x - (this.track.scrollLeft - (this.continuous ? this.period || 0 : 0))) < Math.abs(this.positions[best] - (this.track.scrollLeft - (this.continuous ? this.period || 0 : 0))) ? i : best, 0);
      this.querySelectorAll('[data-carousel-dots] button').forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
      const prev = this.querySelector('[data-carousel-prev]'), next = this.querySelector('[data-carousel-next]');
      if (prev) prev.disabled = this.index === 0;
      if (next) next.disabled = this.index === this.count - 1;
    }
    go(index, manual) {
      if (!this.positions?.length) return;
      this.index = Math.max(0, Math.min(this.count - 1, index));
      if (this.continuous) { this.position = (this.period || 0) + this.positions[this.index]; this.normalize(true); this.update(); if(manual)this.interact(1400); return; }
      this.track.scrollTo({ left: this.positions[this.index], behavior: motion.matches ? 'instant' : 'smooth' });
      if (manual) this.restart();
    }
    next(automatic = false) { this.go(automatic && this.index === this.count - 1 ? 0 : this.index + 1, !automatic); }
    disconnectedCallback() { super.disconnectedCallback(); cancelAnimationFrame(this.frame); cancelAnimationFrame(this.loopFrame); clearTimeout(this.resumeTimer); clearTimeout(this.clickTimer); this.track?.querySelectorAll('[data-loop-clone]').forEach(node=>node.remove()); }
  }
  if (!customElements.get('hamdoo-hero')) customElements.define('hamdoo-hero', HamdooHero);
  if (!customElements.get('hamdoo-carousel')) customElements.define('hamdoo-carousel', HamdooCarousel);
})();
